import React from 'react'
import { 
  EyeIcon,
  RocketLaunchIcon,
  ShieldCheckIcon,
  HandRaisedIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  XCircleIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline'
import Card from '../../components/common/Card'

const InvestorCharterPage: React.FC = () => {
  const investorRights = [
    'Fair Treatment: Receive equitable and unbiased research services.',
    'Timely Redressal: Expect resolution of grievances filed in SEBI Complaints Redress System (SCORES) in a time-bound manner.',
    'Quality Services: Obtain quality services from SEBI-recognized intermediaries, including the right to exit at fair and reasonable terms.',
    'Confidentiality: Have their personal and financial information kept confidential, unless disclosure is required by law or with explicit consent.',
    'Transparency: Access clear and comprehensive disclosures regarding research methodologies, potential conflicts of interest, and compensation structures.'
  ]

  const investorResponsibilities = [
    'Due Diligence: Conduct thorough research and understand the risks involved before making investment decisions.',
    'Engagement: Actively engage with the provided research materials and seek clarification when necessary.',
    'Feedback: Provide constructive feedback to improve the quality of research services.',
    'Compliance: Ensure compliance with all applicable laws and regulations while acting upon research recommendations.'
  ]

  const dosList = [
    'Understand Documents: Read and understand the research reports and related documents carefully before acting upon them.',
    'Know Risks: Be aware of the risks involved in investing in securities.',
    'Monitor Investments: Regularly monitor investment portfolios and promptly report any discrepancies.',
    'Preserve Documents: Keep records of all transactions and communications for future reference.'
  ]

  const dontsList = [
    'Avoid Unverified Sources: Do not rely on unverified or unsolicited investment advice.',
    'Misuse of Information: Refrain from using research materials for purposes other than personal investment decisions.',
    'Neglect Responsibilities: Do not ignore the importance of conducting due diligence before making investment decisions.'
  ]

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-green-600 to-green-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <ShieldCheckIcon className="w-16 h-16 mx-auto mb-6 text-green-200" aria-hidden="true" focusable="false" />
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Investor Charter
            </h1>
            <h2 className="text-2xl md:text-3xl font-semibold mb-4 text-green-100">
              AnalyzerView Market Research
            </h2>
            <div className="bg-green-700/30 rounded-lg p-6 max-w-5xl mx-auto mb-6">
              <div className="grid md:grid-cols-2 gap-4 text-green-100">
                <div>
                  <p className="font-semibold">Name of Research Analyst / Proprietor:</p>
                  <p>Rahul Das</p>
                </div>
                <div>
                  <p className="font-semibold">Entity Name:</p>
                  <p>AnalyzerView Market Research</p>
                </div>
                <div>
                  <p className="font-semibold">Category of Registration:</p>
                  <p>Individual</p>
                </div>
                <div>
                  <p className="font-semibold">SEBI Registration No.:</p>
                  <p>INH000023719</p>
                </div>
                <div>
                  <p className="font-semibold">BSE RAASB Enlistment No.:</p>
                  <p>7041</p>
                </div>
                <div>
                  <p className="font-semibold">Compliance Officer:</p>
                  <p>Rahul Das — compliance@analyzerview.com</p>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-green-600">
                <p className="text-sm text-green-200">
                  Prepared under: Regulation 18 of SEBI (Research Analyst) Regulations, 2014, and SEBI Circular No. SEBI/HO/MIRSD/MIRSD-PoD/P/CIR/2025/81 dated June 2, 2025
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8">
            <Card className="bg-gray-800 border-gray-700">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                  <EyeIcon className="w-8 h-8 text-green-400" aria-hidden="true" focusable="false" />
                </div>
                <h3 className="text-3xl font-bold text-white mb-4">1. Vision</h3>
                <p className="text-gray-300 leading-relaxed">
                  To be recognized as a premier SEBI-registered equity research platform in India, 
                  distinguished by its ethical practices, intellectual rigor, and unwavering commitment 
                  to investor empowerment.
                </p>
              </div>
            </Card>

            <Card className="bg-gray-800 border-gray-700">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                  <RocketLaunchIcon className="w-8 h-8 text-green-400" aria-hidden="true" focusable="false" />
                </div>
                <h3 className="text-3xl font-bold text-white mb-4">2. Mission</h3>
                <p className="text-gray-300 leading-relaxed">
                  To empower investors with accurate, timely, and regulation-compliant equity research. 
                  We are dedicated to providing insightful, data-driven analysis supported by robust 
                  methodologies, advanced technological tools, and a steadfast adherence to investor-centric 
                  policies, thereby fostering a culture of responsible and well-informed investing.
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Rights & Responsibilities Section */}
      <section className="py-20 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8">
            <Card className="bg-gray-900 border-gray-700">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-green-900/30 rounded-full flex items-center justify-center mr-4">
                  <ShieldCheckIcon className="w-6 h-6 text-green-400" aria-hidden="true" focusable="false" />
                </div>
                <h3 className="text-2xl font-bold text-white">3. Investor Rights</h3>
              </div>
              <p className="text-gray-300 mb-4">Investors have the right to:</p>
              <ul className="space-y-3 list-none">
                {investorRights.map((right, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <div className="w-1.5 h-1.5 bg-green-400 rounded-full mt-2 flex-shrink-0" aria-hidden="true"></div>
                    <p className="text-gray-300 leading-relaxed">{right}</p>
                  </li>
                ))}
              </ul>
            </Card>

            <Card className="bg-gray-900 border-gray-700">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-green-900/30 rounded-full flex items-center justify-center mr-4">
                  <HandRaisedIcon className="w-6 h-6 text-green-400" aria-hidden="true" focusable="false" />
                </div>
                <h3 className="text-2xl font-bold text-white">4. Investor Responsibilities</h3>
              </div>
              <p className="text-gray-300 mb-4">Investors are encouraged to:</p>
              <ul className="space-y-3 list-none">
                {investorResponsibilities.map((responsibility, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <div className="w-1.5 h-1.5 bg-green-400 rounded-full mt-2 flex-shrink-0" aria-hidden="true"></div>
                    <p className="text-gray-300 leading-relaxed">{responsibility}</p>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* Grievance Redressal Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-gray-800 border-gray-700">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <ChatBubbleLeftRightIcon className="w-8 h-8 text-green-400" aria-hidden="true" focusable="false" />
              </div>
              <h3 className="text-3xl font-bold text-white mb-4">5. Grievance Redressal Mechanism</h3>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-green-900/20 border border-green-700 rounded-lg p-6">
                <h4 className="font-semibold text-green-300 mb-2">Level 1: Direct Contact</h4>
                <p className="text-gray-300 text-sm">
                  Email: rahul@analyzerview.com<br />
                  Phone: +91 93820 93168
                </p>
              </div>
              <div className="bg-green-900/20 border border-green-700 rounded-lg p-6">
                <h4 className="font-semibold text-green-300 mb-2">Level 2: SEBI SCORES</h4>
                <p className="text-gray-300 text-sm">
                  Online complaint portal:<br />
                  www.scores.sebi.gov.in
                </p>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Do's and Don'ts Section */}
      <section className="py-20 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8">
            <Card className="border-l-4 border-green-500 bg-gray-900">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-green-900/30 rounded-full flex items-center justify-center mr-4">
                  <CheckCircleIcon className="w-6 h-6 text-green-400" aria-hidden="true" focusable="false" />
                </div>
                <h3 className="text-2xl font-bold text-white">6. Do's for Investors</h3>
              </div>
              <ul className="space-y-4 list-none">
                {dosList.map((item, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircleIcon className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" aria-hidden="true" focusable="false" />
                    <p className="text-gray-300 leading-relaxed">{item}</p>
                  </li>
                ))}
              </ul>
            </Card>

            <Card className="border-l-4 border-red-500 bg-gray-900">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-red-900/30 rounded-full flex items-center justify-center mr-4">
                  <XCircleIcon className="w-6 h-6 text-red-400" aria-hidden="true" focusable="false" />
                </div>
                <h3 className="text-2xl font-bold text-white">7. Don'ts for Investors</h3>
              </div>
              <ul className="space-y-4 list-none">
                {dontsList.map((item, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <XCircleIcon className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" aria-hidden="true" focusable="false" />
                    <p className="text-gray-300 leading-relaxed">{item}</p>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Information Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-gray-800 border-gray-700">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <PhoneIcon className="w-8 h-8 text-green-400" aria-hidden="true" focusable="false" />
              </div>
              <h3 className="text-3xl font-bold text-white mb-4">8. Contact Information</h3>
              <p className="text-gray-300 mb-6">
                For any queries or concerns, investors can reach out to:
              </p>
            </div>
            <ul className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto list-none">
              <li className="text-center">
                <div className="text-2xl mb-2" aria-hidden="true">📧</div>
                <div className="text-sm font-medium text-white">Email</div>
                <div className="text-sm text-gray-300">rahul@analyzerview.com</div>
              </li>
              <li className="text-center">
                <div className="text-2xl mb-2" aria-hidden="true">📞</div>
                <div className="text-sm font-medium text-white">Phone</div>
                <div className="text-sm text-gray-300">+91 93820 93168</div>
              </li>
              <li className="text-center">
                <div className="text-2xl mb-2" aria-hidden="true">🏢</div>
                <div className="text-sm font-medium text-white">Address</div>
                <div className="text-sm text-gray-300">Kukshimla, Raidogachia, Purbasthali, Purba Bardhaman, West Bengal – 713513</div>
              </li>
            </ul>
          </Card>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="py-20 bg-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-gray-900 border-gray-700">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <ShieldCheckIcon className="w-8 h-8 text-green-400" aria-hidden="true" focusable="false" />
              </div>
              <h3 className="text-3xl font-bold text-white mb-6">9. Compliance and Disclaimers</h3>
            </div>
            <div className="space-y-6">
              <div className="bg-green-900/20 border border-green-700 rounded-lg p-6">
                <p className="text-green-300 font-semibold mb-2">SEBI Registration</p>
                <p className="text-gray-300 text-sm">
                  AnalyzerView Market Research is registered with SEBI as a Research Analyst under Registration No. INH000023719 and enlisted with BSE RAASB (Enlistment No. 7041) under the SEBI (Research Analysts) Regulations, 2014.
                </p>
              </div>
              <div className="bg-green-900/20 border border-green-700 rounded-lg p-6">
                <p className="text-green-300 font-semibold mb-2">Investment Risks</p>
                <p className="text-gray-300 text-sm">
                  All investments in securities are subject to market risks. Past performance does not guarantee future results. Please read all documents carefully before investing.
                </p>
              </div>
              <div className="bg-green-900/20 border border-green-700 rounded-lg p-6">
                <p className="text-green-300 font-semibold mb-2">No Guarantee</p>
                <p className="text-gray-300 text-sm">
                  Registration with SEBI does not guarantee performance or provide assurance of returns to investors. Research recommendations are based on analysis and should not be considered as investment advice.
                </p>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-20 bg-gradient-to-r from-green-600 to-green-800 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Your Investment Journey Starts Here
          </h2>
          <p className="text-xl mb-8 text-green-100">
            Join thousands of informed investors who trust AnalyzerView for ethical, 
            transparent, and regulation-compliant equity research.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-green-600 bg-white hover:bg-gray-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-900"
            >
              Contact Us
            </a>
            <a
              href="/pricing"
              className="inline-flex items-center px-6 py-3 border border-white text-base font-medium rounded-md text-white hover:bg-green-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-900"
            >
              View Our Services
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}

export default InvestorCharterPage